import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import {
  Button,
  KPICard,
  ConfirmDialog,
  ActionToolbar,
  DataNavigator,
  StatusBadge,
  Badge
} from '@/components/ui';
import { useConstructionBoqs } from './hooks/useConstructionBoqs';
import { useBoqMutations } from './hooks/useBoqMutations';
import { useAuth } from '@/core/auth/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { AuthButton } from '@/core/permissions/AuthButton';
import { boqStatusMap } from '@/utils/status';

// Components
import { BoqFormModal } from './components/BoqFormModal';
import type { ConstructionBoq } from './types/construction-boq.types';
import {
  FileSpreadsheet,
  Plus,
  Download,
  Layers,
  DollarSign,
  FileText,
  CheckCircle2,
  Edit,
  Trash2,
  Eye,
  Building2,
  Package
} from 'lucide-react';

export default function BoqListPage() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const [showCreate, setShowCreate] = useState(false);
  const [editBoq, setEditBoq] = useState<ConstructionBoq | null>(null);
  const [deleteBoq, setDeleteBoq] = useState<ConstructionBoq | null>(null);

  const {
    boqs,
    total,
    isLoading,
    filters,
    setFilters,
  } = useConstructionBoqs(companyId);

  const { createBoq, updateBoq, deleteBoq: deleteBoqMutation } = useBoqMutations(companyId);

  const handleSave = async (data: Partial<ConstructionBoq>) => {
    try {
      if (editBoq) {
        await updateBoq({ id: editBoq.id, updates: data });
        toast.success('Cập nhật BOQ thành công');
      } else {
        await createBoq(data);
        toast.success('Khởi tạo BOQ mới thành công');
      }
      setShowCreate(false);
      setEditBoq(null);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const handleDelete = async () => {
    if (deleteBoq) {
      try {
        await deleteBoqMutation(deleteBoq.id);
        toast.success('Đã xóa BOQ thành công');
        setDeleteBoq(null);
      } catch (error: any) {
        toast.error('Lỗi khi xóa: ' + error.message);
      }
    }
  };

  // Stats
  const stats = useMemo(() => {
    const totalValue = boqs.reduce((sum, item) => sum + (item.total_amount || 0), 0);
    const draftCount = boqs.filter(b => b.status_code === 'draft').length;
    const approvedCount = boqs.filter(b => b.status_code === 'approved').length;
    return { totalValue, draftCount, approvedCount, totalCount: total };
  }, [boqs, total]);

  const columns = useMemo(() => [
    {
      key: 'boq_info',
      title: 'BOQ / Dự án',
      render: (_: any, item: ConstructionBoq) => (
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-slate-400  tracking-widest">{item.boq_code}</span>
          <span className="font-black text-slate-900 leading-tight">{item.boq_name}</span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Building2 className="w-3 h-3 text-slate-400" />
            <span className="text-[10px] text-slate-400 font-bold ">{item.project?.name}</span>
          </div>
        </div>
      )
    },
    {
      key: 'details',
      title: 'Chi tiết',
      render: (_: any, item: ConstructionBoq) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <Layers className="w-3 h-3 text-slate-400" />
            <span className="text-xs font-bold text-slate-700">{(item as any).item_count || 0} Hạng mục</span>
          </div>
          <span className="text-[10px] text-slate-400 font-black  tracking-tighter mt-0.5">V: {item.version_no || '1.0'}</span>
        </div>
      )
    },
    {
      key: 'value',
      title: 'Giá trị dự toán',
      render: (_: any, item: ConstructionBoq) => (
        <div className="flex flex-col">
          <span className="font-black text-blue-600">
            {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(item.total_amount || 0)}
          </span>
          <span className="text-[10px] text-slate-400 font-bold ">Ngày tạo: {new Date(item.created_at).toLocaleDateString('vi-VN')}</span>
        </div>
      )
    },
    {
      key: 'status',
      title: 'Trạng thái',
      render: (_: any, item: ConstructionBoq) => (
        <StatusBadge status={item.status_code} type="default" className="font-black" />
      )
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, item: ConstructionBoq) => (
        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={() => navigate(`./detail/${item.id}`)}>
            <Eye className="w-4 h-4" />
          </Button>
          <AuthButton
            module="boq"
            action="update"
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditBoq(item);
              setShowCreate(true);
            }}
          >
            <Edit className="w-4 h-4" />
          </AuthButton>
          <AuthButton
            module="boq"
            action="delete"
            variant="ghost"
            size="sm"
            className="text-red-500 hover:bg-red-50"
            onClick={() => setDeleteBoq(item)}
          >
            <Trash2 className="w-4 h-4" />
          </AuthButton>
        </div>
      )
    }
  ], [navigate]);

  const mobileCardRender = (item: ConstructionBoq) => (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-slate-400  tracking-widest">{item.boq_code}</span>
          <h3 className="font-black text-slate-900 text-lg leading-tight">{item.boq_name}</h3>
        </div>
        <StatusBadge status={item.status_code} type="default" size="sm" />
      </div>

      <div className="bg-white/50 p-3 rounded-2xl border border-white shadow-sm space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <Building2 className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-bold text-slate-700">{item.project?.name}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Layers className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-medium text-slate-500">{(item as any).item_count || 0} hạng mục bóc tách</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="flex flex-col">
          <span className="text-[8px] font-black text-slate-400  tracking-widest">Tổng dự toán</span>
          <span className="text-sm font-black text-blue-600">
             {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(item.total_amount || 0)}
          </span>
        </div>
        <div className="flex gap-2">
           <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-xl" onClick={() => navigate(`./detail/${item.id}`)}>
             <Eye className="w-4 h-4 text-slate-500" />
           </Button>
           <AuthButton
              module="boq"
              action="update"
              variant="primary"
              size="sm"
              className="h-9 px-4 rounded-xl font-black text-[10px]  tracking-widest"
              onClick={() => {
                setEditBoq(item);
                setShowCreate(true);
              }}
            >
              Sửa
            </AuthButton>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Bóc tách Khối lượng (BOQ)"
      description="Quản lý định mức vật tư, nhân công và thiết bị thi công"
      breadcrumbs={[{ label: 'Thi công' }, { label: 'BOQ' }]}
    >
      <div className="space-y-6">
        {/* KPI Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng giá trị dự toán"
            value={stats.totalValue}
            icon={DollarSign}
            variant="primary"
            format="compact-currency"
          />
          <KPICard
            title="Đã phê duyệt"
            value={stats.approvedCount}
            icon={CheckCircle2}
            variant="success"
          />
          <KPICard
            title="Bản nháp"
            value={stats.draftCount}
            icon={FileText}
            variant="warning"
          />
          <KPICard
            title="Tổng số BOQ"
            value={stats.totalCount}
            icon={Package}
            variant="info"
          />
        </div>

        {/* Action Toolbar */}
        <ActionToolbar
          searchValue={filters.search}
          onSearchChange={(v) => setFilters({ ...filters, search: v })}
          searchPlaceholder="Tìm mã BOQ, tên, dự án..."
          filterOptions={[
            {
              id: 'status', label: 'Trạng thái', type: 'select', choices: [
                { value: 'all', label: 'Tất cả trạng thái' },
                ...Object.entries(boqStatusMap).map(([val, cfg]) => ({ value: val, label: cfg.label }))
              ]
            }
          ]}
          onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
          actions={
            <Button
              variant="outline"
              size="md"
              className="rounded-2xl mr-2"
              onClick={() => navigate('/construction/boq/import')}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import Excel
            </Button>
          }
          primaryActionLabel="Tạo BOQ mới"
          primaryActionIcon={<Plus className="w-4 h-4" />}
          onPrimaryAction={() => setShowCreate(true)}
          isLoading={isLoading}
        />

        {/* Data Navigator */}
        <DataNavigator
          columns={columns}
          data={boqs}
          isLoading={isLoading}
          onRowClick={(item) => navigate(`./detail/${item.id}`)}
          mobileCardRender={mobileCardRender}
        />
      </div>

      <BoqFormModal
        isOpen={showCreate || !!editBoq}
        onClose={() => {
          setShowCreate(false);
          setEditBoq(null);
        }}
        onSave={handleSave}
        item={editBoq}
        companyId={companyId}
      />

      <ConfirmDialog
        isOpen={!!deleteBoq}
        title="Xác nhận xóa BOQ"
        message={`Bạn có chắc chắn muốn xóa BOQ "${deleteBoq?.boq_name}"? Toàn bộ hạng mục bên trong cũng sẽ bị xóa. Hành động này không thể hoàn tác.`}
        confirmText="Xác nhận xóa"
        onConfirm={handleDelete}
        onClose={() => setDeleteBoq(null)}
        isDestructive
      />
    </PageLayout>
  );
}
